/**
 * Zuhause — der leise Gruß. (08.10.2026)
 *
 * Martin, vor dem Schlafengehen: „Überrasche mich noch mit etwas Liebem,
 * vielleicht in dem Bild im Zuhause.“ Das Bild trägt dafür ein winziges
 * Detail (tagesszene.ts, `stillesDetail`); hier steht der Gruß dazu — EINE
 * Zeile, die beim Öffnen sanft einblendet und dann ruhig stehen bleibt:
 * „Schön, dass du da bist.“ am Morgen (die Überschrift sagt schon „Guten
 * Morgen, Martin“), „Gute Nacht, Martin.“ zur Nacht.
 *
 * Regeln:
 *   - Die Texte stehen NUR hier (WILLKOMMEN_TEXT). Die Ansicht liest von hier.
 *   - Morgens 05:00–09:59 Wiener Zeit: der Morgengruß. Ab 21:00 bis 04:59:
 *     „Gute Nacht“. Sonst nichts — tagsüber bleibt das Haus still.
 *   - Höchstens EINMAL je Tag. Der Tag beginnt hier um 05:00, nicht um
 *     Mitternacht: wer um 23:30 „Gute Nacht“ liest und um 00:30 noch einmal
 *     öffnet, bekommt keinen zweiten Gruß — und am nächsten Morgen wieder
 *     den neuen.
 *   - Der Merker liegt in localStorage. Ist der Speicher gesperrt (privates
 *     Fenster, Einstellung), gilt ein Merker im Arbeitsspeicher: dann höchstens
 *     einmal je Seitenaufruf. Die Seite funktioniert in jedem Fall; der Gruß
 *     ist ein Geschenk, keine Pflicht.
 *   - Kein Netz, kein Server.
 */
import { stundeInWien, wienTagSchluessel } from "./zuhause.js";

type WillkommenArt = "morgen" | "nacht";

/** Die Wörter des Grußes — ohne Namen; `willkommenText` setzt ihn ein. */
const WILLKOMMEN_TEXT: Readonly<Record<WillkommenArt, { mitName: (name: string) => string; ohneName: string }>> = {
  // Am Morgen sagt die Überschrift darüber schon „Guten Morgen, Martin“ —
  // die Zeile wiederholt das nicht, sie sagt nur das Liebe dazu.
  morgen: {
    mitName: () => "Schön, dass du da bist.",
    ohneName: "Schön, dass du da bist.",
  },
  nacht: {
    mitName: (name) => `Gute Nacht, ${name}.`,
    ohneName: "Gute Nacht.",
  },
};

/** Der Gruß am Morgen gilt von 05:00 bis vor 10:00 (Wiener Stunde). */
const MORGEN_AB_STUNDE = 5;
const MORGEN_BIS_STUNDE = 10;
/** „Gute Nacht“ gilt ab 21:00 und bis vor 05:00. */
const NACHT_AB_STUNDE = 21;

/** Unter diesem Schlüssel steht der Tag des letzten Grußes (JJJJ-MM-TT). */
export const WILLKOMMEN_SCHLUESSEL = "vespera.willkommen";

/** Welcher Gruß passt zur Uhrzeit? `null` = keiner (tagsüber). */
export function willkommenArt(jetzt: Date = new Date()): WillkommenArt | null {
  const h = stundeInWien(jetzt);
  if (h >= MORGEN_AB_STUNDE && h < MORGEN_BIS_STUNDE) return "morgen";
  if (h >= NACHT_AB_STUNDE || h < MORGEN_AB_STUNDE) return "nacht";
  return null;
}

/**
 * Der Tag, an dem der Merker hängt: das Wiener Datum, nur beginnt der Tag um
 * 05:00. Von 00:00 bis 04:59 gilt noch das Datum von gestern.
 */
export function willkommenTag(jetzt: Date = new Date()): string {
  const vorDemMorgen = stundeInWien(jetzt) < MORGEN_AB_STUNDE;
  return wienTagSchluessel(vorDemMorgen ? new Date(jetzt.getTime() - 86_400_000) : jetzt);
}

/** Der Text zum Gruß; ohne Namen fällt er auf die kurze Form zurück. */
export function willkommenText(art: WillkommenArt, name?: string): string {
  const n = (name ?? "").trim();
  const t = WILLKOMMEN_TEXT[art];
  return n ? t.mitName(n) : t.ohneName;
}

/** Wo der Merker liegt: localStorage, sonst (gesperrt) der Arbeitsspeicher. */
export interface WillkommenSpeicher {
  lies(): string | null;
  schreibe(tag: string): void;
}

let imArbeitsspeicher: string | null = null;

export const BROWSER_SPEICHER: WillkommenSpeicher = {
  lies() {
    try {
      return localStorage.getItem(WILLKOMMEN_SCHLUESSEL) ?? imArbeitsspeicher;
    } catch {
      return imArbeitsspeicher;
    }
  },
  schreibe(tag) {
    imArbeitsspeicher = tag;
    try {
      localStorage.setItem(WILLKOMMEN_SCHLUESSEL, tag);
    } catch {
      /* gesperrt — der Arbeitsspeicher trägt den Merker bis zum Neuladen */
    }
  },
};

/** Nur für Tests: den Merker im Arbeitsspeicher vergessen. */
export function vergissWillkommenImArbeitsspeicher(): void {
  imArbeitsspeicher = null;
}

/**
 * Den Gruß HOLEN — und im selben Zug als gesehen merken. Liefert `null`,
 * wenn jetzt kein Gruß dran ist oder heute schon einer kam.
 *
 * ⚠️ Der Merker wird beim Holen geschrieben, nicht erst, wenn die Zeile
 *    sichtbar ist: so kann ein zweiter Aufbau der Seite (Umschalten,
 *    Neuzeichnen) den Gruß nicht ein zweites Mal zeigen.
 */
export function nimmWillkommen(
  jetzt: Date = new Date(),
  name?: string,
  speicher: WillkommenSpeicher = BROWSER_SPEICHER,
): { art: WillkommenArt; text: string } | null {
  const art = willkommenArt(jetzt);
  if (!art) return null;
  const tag = willkommenTag(jetzt);
  let zuletzt: string | null;
  try {
    zuletzt = speicher.lies();
  } catch {
    zuletzt = null;
  }
  if (zuletzt === tag) return null;
  try {
    speicher.schreibe(tag);
  } catch {
    /* ohne Merker gilt: höchstens dieser eine Aufruf */
  }
  return { art, text: willkommenText(art, name) };
}
